import React from 'react';
import {
  chipStyle,
  chipWrapStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  summaryCardStyle,
} from './dashboardStyles';

function issueCardStyle() {
  return {
    border: '1px solid rgba(220,38,38,0.35)',
    borderRadius: 8,
    background: 'rgba(220,38,38,0.06)',
    padding: '9px 10px',
  };
}

function warningCardStyle() {
  return {
    border: '1px solid rgba(217,119,6,0.35)',
    borderRadius: 8,
    background: 'rgba(217,119,6,0.07)',
    padding: '9px 10px',
  };
}

function summaryPillStyle(color) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    border: `1px solid ${color}`,
    color,
    background: 'var(--bg-card)',
    padding: '3px 8px',
    fontSize: 12,
    fontWeight: 900,
  };
}

function actionSummaryStyle(severity = 'warning') {
  const color = severity === 'danger' ? '#dc2626' : '#d97706';
  return {
    border: `1px solid ${color}`,
    borderRadius: 8,
    background: severity === 'danger' ? 'rgba(220,38,38,0.05)' : 'rgba(217,119,6,0.06)',
    padding: '8px 10px',
    minWidth: 180,
  };
}

function actionBadgeStyle(severity = 'warning') {
  const color = severity === 'danger' ? '#dc2626' : '#d97706';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px solid ${color}`,
    borderRadius: 6,
    color,
    background: 'var(--bg-card)',
    padding: '2px 7px',
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.2,
  };
}

function actionButtonStyle(disabled, tone = 'default') {
  const colors = {
    default: ['var(--border)', 'var(--bg-card)', 'var(--text)'],
    info: ['#2563eb', 'rgba(37,99,235,0.08)', '#2563eb'],
    warning: ['#d97706', 'rgba(217,119,6,0.08)', '#d97706'],
    danger: ['#dc2626', 'rgba(220,38,38,0.08)', '#dc2626'],
  };
  const [border, background, color] = colors[tone] || colors.default;
  return {
    border: `1px solid ${disabled ? 'var(--border)' : border}`,
    borderRadius: 6,
    background: disabled ? 'var(--bg-subtle)' : background,
    color: disabled ? 'var(--text-muted)' : color,
    padding: '5px 8px',
    fontSize: 12,
    fontWeight: 850,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
  };
}

function resolveLedgerAction(issue, module) {
  if (!module) return null;
  if (
    issue.type === 'enabled_without_assignee'
    && module.isEnabled
    && issue.recommendation?.actionType === 'disable_candidate'
  ) {
    return { label: '원장 끄기', tone: 'warning' };
  }
  if (issue.type === 'disabled_but_assigned' && !module.isEnabled) {
    return { label: '원장 켜기', tone: 'danger' };
  }
  return null;
}

function candidateActions(issue, module) {
  if (!module || issue.type !== 'enabled_without_assignee' || !module.isEnabled) return [];
  if (issue.recommendation?.actionType === 'sales_worker_required') {
    return [
      { key: 'sales-worker-later', label: '영업자 설계로 보류', tone: 'warning', note: '[SHADOW_SALES_WORKER_REQUIRED] Shadow 정리: 미팅지도는 sales 영업자 계정 설계 후 검토' },
      { key: 'keep', label: '유지로 표시', tone: 'info', note: '[SHADOW_KEEP] Shadow 정리: 유지' },
    ];
  }
  const actions = [
    { key: 'keep', label: '유지로 표시', tone: 'info', note: '[SHADOW_KEEP] Shadow 정리: 유지' },
    { key: 'later', label: '나중에 검토', tone: 'default', note: '[SHADOW_REVIEW_LATER] Shadow 정리: 나중에 검토' },
  ];
  if (issue.recommendation?.actionType === 'assign_candidate') {
    actions.unshift({ key: 'assign', label: '배정 필요 표시', tone: 'info', note: '[SHADOW_ASSIGN] Shadow 정리: 직원 배정 필요' });
  }
  if (issue.recommendation?.actionType === 'disable_candidate') {
    actions.unshift({ key: 'disable-candidate', label: '끄기 후보 표시', tone: 'warning', note: '[SHADOW_DISABLE_CANDIDATE] Shadow 정리: 원장 끄기 후보' });
  }
  return actions;
}

export default function TenantModuleEnforcementPanel({
  tenant,
  modules = [],
  savingKey = '',
  previewLoadingKey = '',
  onToggle,
  onMark,
  onPreview,
}) {
  const issues = tenant?.shadowIssues || tenant?.enforcementIssues || [];
  const summary = tenant?.shadowSummary || {};
  const actionSummary = tenant?.shadowActionSummary || [];
  const moduleMap = new Map((modules || []).map((module) => [module.code, module]));
  if (!issues.length) {
    return (
      <div style={{ ...summaryCardStyle, padding: 12 }}>
        <h3 style={panelTitleStyle}>Shadow 검증</h3>
        <div style={panelSubStyle}>
          고객사 기능 원장과 현재 직원 권한 노출이 모두 충돌 없이 맞습니다. 실제 권한은 변경하지 않았습니다.
        </div>
        <div style={{ ...chipWrapStyle, marginTop: 8 }}>
          <span style={summaryPillStyle('#16a34a')}>정상 {summary.okCount ?? tenant?.moduleCount ?? 0}개</span>
          <span style={summaryPillStyle('#d97706')}>주의 0개</span>
          <span style={summaryPillStyle('#dc2626')}>위험 0개</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...summaryCardStyle, padding: 12 }}>
      <h3 style={panelTitleStyle}>Shadow 검증</h3>
      <div style={panelSubStyle}>
        아직 실제 권한을 자동 변경하지 않습니다. 아래 항목은 SaaS 원장 기준과 현재 직원 메뉴 노출을 비교한 결과입니다.
      </div>
      <div style={{ ...chipWrapStyle, marginTop: 8 }}>
        <span style={summaryPillStyle('#16a34a')}>정상 {summary.okCount || 0}개</span>
        <span style={summaryPillStyle('#d97706')}>주의 {summary.warningCount || 0}개</span>
        <span style={summaryPillStyle('#dc2626')}>위험 {summary.dangerCount || 0}개</span>
      </div>
      {!!actionSummary.length && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 10 }}>
          {actionSummary.map((item) => (
            <div
              key={item.actionType}
              style={actionSummaryStyle(item.dangerCount > 0 ? 'danger' : 'warning')}
            >
              <div style={rowTitleStyle}>{item.actionLabel}</div>
              <div style={rowMetaStyle}>
                총 {item.count}개 · 주의 {item.warningCount || 0}개 · 위험 {item.dangerCount || 0}개
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {issues.map((issue) => {
          const module = moduleMap.get(issue.moduleCode);
          const key = `${tenant.code}:${issue.moduleCode}`;
          const busy = savingKey === key || previewLoadingKey === key;
          const ledgerAction = resolveLedgerAction(issue, module);
          const markActions = candidateActions(issue, module);
          return (
            <div key={issue.moduleCode} style={issue.severity === 'danger' ? issueCardStyle() : warningCardStyle()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={rowTitleStyle}>{issue.moduleName}</div>
                  <div style={rowMetaStyle}>
                    {issue.label} · 영향 직원 {issue.affectedUserCount || 0}명
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {module && (
                    <button
                      type="button"
                      style={actionButtonStyle(busy, 'info')}
                      disabled={busy}
                      onClick={() => onPreview?.(tenant, module)}
                    >
                      {previewLoadingKey === key ? '확인 중' : '권한 미리보기'}
                    </button>
                  )}
                  {ledgerAction && (
                    <button
                      type="button"
                      style={actionButtonStyle(busy, ledgerAction.tone)}
                      disabled={busy}
                      onClick={() => onToggle?.(tenant, module, { note: `Shadow 검증에서 ${ledgerAction.label}` })}
                    >
                      {savingKey === key ? '저장 중' : ledgerAction.label}
                    </button>
                  )}
                  {markActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      style={actionButtonStyle(busy, action.tone)}
                      disabled={busy}
                      onClick={() => onMark?.(tenant, module, { note: action.note, label: action.label })}
                    >
                      {savingKey === key ? '저장 중' : action.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ ...rowMetaStyle, marginTop: 4 }}>{issue.detail}</div>
              {issue.recommendation && (
                <div style={{ marginTop: 7 }}>
                  <span style={actionBadgeStyle(issue.severity)}>{issue.recommendation.actionLabel}</span>
                  <div style={{ ...rowMetaStyle, marginTop: 5 }}>{issue.recommendation.actionDetail}</div>
                  <div style={chipWrapStyle}>
                    {issue.recommendation.primaryAction && <span style={chipStyle}>{issue.recommendation.primaryAction}</span>}
                    {issue.recommendation.secondaryAction && <span style={chipStyle}>{issue.recommendation.secondaryAction}</span>}
                  </div>
                </div>
              )}
              <div style={chipWrapStyle}>
                {(issue.affectedEmployees || []).map((user) => (
                  <span key={`${issue.moduleCode}-${user.id}`} style={chipStyle}>
                    {user.name} · {user.loginId}
                  </span>
                ))}
                {(issue.affectedUserCount || 0) > (issue.affectedEmployees || []).length && (
                  <span style={chipStyle}>외 {(issue.affectedUserCount || 0) - issue.affectedEmployees.length}명</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
